import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sliders, Save, Trash2, Target, TrendingDown, IndianRupee, Gauge, FileDown } from "lucide-react";
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, ReferenceLine, Legend, BarChart, Bar
} from "recharts";
import {
    PERSONAS, simulate, loadTargets, saveTargets,
    loadScenarios, saveScenario, deleteScenario
} from "@/lib/simulator";
import { exportSimulatorPDF } from "@/lib/pdfExport";
import LiveFeed from "@/components/simulator/LiveFeed";

const PERSONA_KEYS = ["msme", "bank", "regulator"];

export default function SimulatorPanel() {
    const [personaKey, setPersonaKey] = useState("msme");
    const [targets, setTargets] = useState({ s1: 20, s2: 30, s3: 15 });
    const [regulatorTargets, setRegulatorTargets] = useState(loadTargets());
    const [scenarios, setScenarios] = useState([]);
    const [scenarioName, setScenarioName] = useState("");
    const [exporting, setExporting] = useState(false);
    const [liveScope2, setLiveScope2] = useState(null);
    const riskChartRef = useRef(null);
    const financialChartRef = useRef(null);

    const handleLiveScope2 = useCallback((v) => setLiveScope2(v), []);

    useEffect(() => {
        setScenarios(loadScenarios(personaKey));
        if (personaKey === "regulator") {
            setTargets({ s1: regulatorTargets.s1, s2: regulatorTargets.s2, s3: regulatorTargets.s3 });
        } else {
            setTargets({ s1: 20, s2: 30, s3: 15 });
        }
        setLiveScope2(null); // reset live override on persona switch
    }, [personaKey]); // eslint-disable-line

    const overrides = useMemo(() => (liveScope2 != null ? { s2: liveScope2 } : {}), [liveScope2]);
    const result = useMemo(() => simulate(personaKey, targets, overrides), [personaKey, targets, overrides]);
    const persona = PERSONAS[personaKey];

    // For MSME / Bank — apply regulator targets as benchmark projection
    const benchmark = useMemo(() => {
        if (personaKey === "regulator") return null;
        return simulate(personaKey, {
            s1: regulatorTargets.s1,
            s2: regulatorTargets.s2,
            s3: regulatorTargets.s3
        }, overrides);
    }, [personaKey, regulatorTargets, overrides]);

    const chartData = useMemo(() => {
        return result.projection.map((p, i) => ({
            year: p.year,
            "Baseline Risk": p.baselineScore,
            "Simulated Risk": p.simulatedScore,
            ...(benchmark ? { "Regulator Target": benchmark.projection[i].simulatedScore } : {}),
            "Annual Savings (₹Cr)": p.savings
        }));
    }, [result, benchmark]);

    const handleSaveScenario = () => {
        const name = scenarioName.trim() || `Plan ${new Date().toLocaleDateString("en-IN")}`;
        const s = {
            id: Date.now(),
            name,
            targets: { ...targets },
            score: result.simulatedScore,
            savingsCr: result.totalSavingsInrCr,
            reduction: result.reductionPct,
            createdAt: new Date().toISOString()
        };
        setScenarios(saveScenario(personaKey, s));
        setScenarioName("");
    };

    const handleDelete = (id) => setScenarios(deleteScenario(personaKey, id));
    const handleLoad = (s) => setTargets(s.targets);

    const handleRegulatorPublish = () => {
        const t = { s1: targets.s1, s2: targets.s2, s3: targets.s3, year: 2030 };
        saveTargets(t);
        setRegulatorTargets(t);
    };

    const handleExportPDF = async () => {
        setExporting(true);
        try {
            await exportSimulatorPDF({
                personaKey,
                targets,
                result,
                regulatorTargets,
                riskChartRef,
                financialChartRef
            });
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="simulator-panel">
            {/* Persona tabs */}
            <div className="lg:col-span-12 flex flex-wrap items-center justify-between gap-4 border-b border-navy/10 pb-4">
                <div className="flex items-center gap-2 flex-wrap">
                    {PERSONA_KEYS.map((k) => {
                        const p = PERSONAS[k];
                        const active = k === personaKey;
                        return (
                            <button
                                key={k}
                                onClick={() => setPersonaKey(k)}
                                data-testid={`persona-tab-${k}`}
                                className={`px-5 py-3 text-[11px] tracking-[0.24em] uppercase font-medium transition-all duration-300 border ${
                                    active
                                        ? "border-gold bg-gold/10 text-gold"
                                        : "border-navy/10 text-navy/60 hover:border-navy/30 hover:text-navy"
                                }`}
                            >
                                {p.label}
                            </button>
                        );
                    })}
                </div>
                <div className="flex items-center gap-4">
                    <div className="hidden md:block font-mono text-[10px] tracking-[0.22em] uppercase text-navy/50">
                        {persona.subtitle}
                    </div>
                    <button
                        onClick={handleExportPDF}
                        disabled={exporting}
                        data-testid="export-pdf-btn"
                        className="btn-ghost !py-2.5 !px-4 !text-[10px] disabled:opacity-50"
                    >
                        <FileDown size={14} /> {exporting ? "Preparing…" : "Export PDF"}
                    </button>
                </div>
            </div>

            {/* Left: sliders + persona summary */}
            <div className="lg:col-span-4 bg-white border border-navy/10 p-8 flex flex-col gap-8">
                <div className="flex items-center justify-between font-mono text-[10px] tracking-[0.22em] uppercase text-navy/50">
                    <span className="flex items-center gap-2"><Sliders size={12} /> Targets · 2030</span>
                    <span>{personaKey === "regulator" ? "Sector-wide" : "Entity-level"}</span>
                </div>

                <SliderRow
                    label="Scope 1 Reduction"
                    scope="s1"
                    value={targets.s1}
                    setValue={(v) => setTargets((t) => ({ ...t, s1: v }))}
                    benchmark={personaKey !== "regulator" ? regulatorTargets.s1 : null}
                />
                <SliderRow
                    label="Scope 2 Reduction"
                    scope="s2"
                    value={targets.s2}
                    setValue={(v) => setTargets((t) => ({ ...t, s2: v }))}
                    benchmark={personaKey !== "regulator" ? regulatorTargets.s2 : null}
                />
                <SliderRow
                    label="Scope 3 Reduction"
                    scope="s3"
                    value={targets.s3}
                    setValue={(v) => setTargets((t) => ({ ...t, s3: v }))}
                    benchmark={personaKey !== "regulator" ? regulatorTargets.s3 : null}
                />

                <div className="pt-4 border-t border-navy/10">
                    <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-navy/50 mb-3">Composite Reduction</div>
                    <div className="flex items-baseline gap-2">
                        <span className="font-serif text-5xl text-gold">{result.reductionPct}%</span>
                        <span className="text-xs font-mono text-navy/50">by 2030</span>
                    </div>
                </div>

                {personaKey === "regulator" ? (
                    <button
                        onClick={handleRegulatorPublish}
                        data-testid="publish-regulator-targets"
                        className="btn-institution w-full justify-center"
                    >
                        <Target size={14} /> Publish Sector Targets
                    </button>
                ) : (
                    <div className="pt-2">
                        <input
                            value={scenarioName}
                            onChange={(e) => setScenarioName(e.target.value)}
                            placeholder="Name this scenario"
                            data-testid="scenario-name-input"
                            className="w-full bg-transparent border border-navy/10 px-4 py-3 text-sm text-navy placeholder:text-navy/30 focus:border-gold outline-none mb-3"
                        />
                        <button onClick={handleSaveScenario} className="btn-institution w-full justify-center" data-testid="save-scenario-btn">
                            <Save size={14} /> Save Scenario
                        </button>
                    </div>
                )}
            </div>

            {/* Center: metrics + charts */}
            <div className="lg:col-span-8 flex flex-col gap-6">
                <LiveFeed
                    personaKey={personaKey}
                    baselineScope2={persona.baseline.s2}
                    onLiveScope2Change={handleLiveScope2}
                />

                {/* KPI row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-navy/10 border border-navy/10">
                    <KPI icon={<Gauge size={14} strokeWidth={1.5} />} label="Simulated Risk" value={result.simulatedScore} suffix={`/ 100`} testId="kpi-risk" trend={result.simulatedScore - result.baselineScore} />
                    <KPI icon={<TrendingDown size={14} strokeWidth={1.5} />} label="Emissions Avoided" value={(result.totalBaseline - result.totalReduced).toLocaleString("en-IN")} suffix="tCO₂e / yr" testId="kpi-emissions" />
                    <KPI icon={<IndianRupee size={14} strokeWidth={1.5} />} label="Annual Savings" value={`₹${result.totalSavingsInrCr}`} suffix="Cr / yr" testId="kpi-savings" />
                    <KPI icon={<Target size={14} strokeWidth={1.5} />} label="COC Uplift Avoided" value={`${result.cocBpsSavings}`} suffix="bps" testId="kpi-coc" />
                </div>

                {/* Risk chart */}
                <div ref={riskChartRef} className="bg-white border border-navy/10 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold">Fig. 01 · Risk Score Trajectory</div>
                        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-navy/40">2025 — 2030</div>
                    </div>
                    <div className="h-[260px] min-h-[260px] w-full">
                        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={220}>
                            <LineChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                <CartesianGrid stroke="rgba(11,31,42,0.08)" vertical={false} />
                                <XAxis dataKey="year" stroke="rgba(11,31,42,0.55)" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
                                <YAxis stroke="rgba(11,31,42,0.55)" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} domain={[0, 100]} />
                                <Tooltip contentStyle={{ backgroundColor: "#FFFFFF", border: "1px solid rgba(11,31,42,0.15)", color: "#0B1F2A", fontFamily: "IBM Plex Mono", fontSize: 11 }} />
                                <Legend wrapperStyle={{ fontFamily: "IBM Plex Mono", fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase" }} />
                                <Line type="monotone" dataKey="Baseline Risk" stroke="rgba(11,31,42,0.55)" strokeDasharray="4 4" strokeWidth={1.5} dot={false} />
                                <Line type="monotone" dataKey="Simulated Risk" stroke="#C89B2A" strokeWidth={2.5} dot={{ r: 3, fill: "#C89B2A" }} />
                                {benchmark && (
                                    <Line type="monotone" dataKey="Regulator Target" stroke="#2E8B85" strokeDasharray="2 4" strokeWidth={1.8} dot={false} />
                                )}
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Financial impact chart */}
                <div ref={financialChartRef} className="bg-white border border-navy/10 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold">Fig. 02 · Financial Impact (₹ Cr / yr)</div>
                        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-navy/40">CBAM + COC Savings</div>
                    </div>
                    <div className="h-[220px] min-h-[220px] w-full">
                        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={180}>
                            <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                <CartesianGrid stroke="rgba(11,31,42,0.08)" vertical={false} />
                                <XAxis dataKey="year" stroke="rgba(11,31,42,0.55)" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
                                <YAxis stroke="rgba(11,31,42,0.55)" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
                                <Tooltip contentStyle={{ backgroundColor: "#FFFFFF", border: "1px solid rgba(11,31,42,0.15)", color: "#0B1F2A", fontFamily: "IBM Plex Mono", fontSize: 11 }} />
                                <Bar dataKey="Annual Savings (₹Cr)" fill="#1F6F6A" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Scenarios */}
                {personaKey !== "regulator" && (
                    <div className="bg-white border border-navy/10 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold">Saved Scenarios</div>
                            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-navy/40">{scenarios.length} / 8</div>
                        </div>
                        {scenarios.length === 0 ? (
                            <div className="text-sm text-navy/40 py-6 text-center font-mono uppercase tracking-[0.2em] text-[10px]">
                                No scenarios yet — adjust sliders & save your first plan
                            </div>
                        ) : (
                            <ul className="divide-y divide-navy/10">
                                <AnimatePresence>
                                    {scenarios.map((s) => (
                                        <motion.li
                                            key={s.id}
                                            initial={{ opacity: 0, y: 8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, x: -20 }}
                                            className="py-3 grid grid-cols-12 items-center gap-3 text-sm"
                                            data-testid={`scenario-${s.id}`}
                                        >
                                            <div className="col-span-4">
                                                <div className="text-navy">{s.name}</div>
                                                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-navy/40 mt-1">
                                                    S1 {s.targets.s1}% · S2 {s.targets.s2}% · S3 {s.targets.s3}%
                                                </div>
                                            </div>
                                            <div className="col-span-2 font-serif text-2xl text-gold">{s.score}</div>
                                            <div className="col-span-2 font-mono text-xs text-navy/70">₹{s.savingsCr} Cr</div>
                                            <div className="col-span-2 font-mono text-xs text-navy/70">-{s.reduction}%</div>
                                            <div className="col-span-2 flex gap-2 justify-end">
                                                <button
                                                    onClick={() => handleLoad(s)}
                                                    className="text-[10px] font-mono tracking-[0.2em] uppercase text-navy/60 hover:text-gold px-3 py-2 border border-navy/10"
                                                    data-testid={`load-scenario-${s.id}`}
                                                >
                                                    Load
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(s.id)}
                                                    className="p-2 text-navy/40 hover:text-red-400 border border-navy/10"
                                                    data-testid={`delete-scenario-${s.id}`}
                                                    aria-label="Delete"
                                                >
                                                    <Trash2 size={12} />
                                                </button>
                                            </div>
                                        </motion.li>
                                    ))}
                                </AnimatePresence>
                            </ul>
                        )}
                    </div>
                )}

                {personaKey === "regulator" && (
                    <div className="bg-white border border-navy/10 p-6">
                        <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold mb-3">Published Sector Targets</div>
                        <div className="grid grid-cols-3 gap-px bg-navy/10">
                            {["s1", "s2", "s3"].map((k, i) => (
                                <div key={k} className="bg-paper p-6" data-testid={`published-target-${k}`}>
                                    <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-navy/40">Scope {i + 1}</div>
                                    <div className="font-serif text-4xl text-teal-light mt-2">-{regulatorTargets[k]}%</div>
                                    <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-navy/40 mt-2">by {regulatorTargets.year}</div>
                                </div>
                            ))}
                        </div>
                        <p className="mt-4 text-xs text-navy/50 leading-relaxed">
                            Published targets appear as a dashed benchmark line on every MSME and Bank simulator — a live compliance north-star.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

function SliderRow({ label, scope, value, setValue, benchmark }) {
    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <label className="font-mono text-[10px] tracking-[0.22em] uppercase text-navy/50">{label}</label>
                <span className="font-mono text-sm text-gold">-{value}%</span>
            </div>
            <div className="relative">
                <input
                    type="range"
                    min={0}
                    max={70}
                    step={1}
                    value={value}
                    onChange={(e) => setValue(Number(e.target.value))}
                    data-testid={`slider-${scope}`}
                    className="w-full h-1 bg-navy/10 appearance-none cursor-pointer accent-gold outline-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-gold [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:bg-gold [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer"
                />
                {benchmark != null && (
                    <div
                        className="absolute top-1/2 -translate-y-1/2 w-[2px] h-4 bg-teal-light pointer-events-none"
                        style={{ left: `${(benchmark / 70) * 100}%` }}
                        title={`Regulator target: -${benchmark}%`}
                    />
                )}
            </div>
            {benchmark != null && (
                <div className="mt-2 font-mono text-[9px] tracking-[0.22em] uppercase text-teal-light">
                    Regulator benchmark: -{benchmark}%
                </div>
            )}
        </div>
    );
}

function KPI({ icon, label, value, suffix, testId, trend }) {
    return (
        <div className="bg-paper p-5" data-testid={testId}>
            <div className="flex items-center gap-2 text-navy/50 mb-3">
                <span className="text-gold">{icon}</span>
                <span className="font-mono text-[10px] tracking-[0.22em] uppercase">{label}</span>
            </div>
            <div className="flex items-baseline gap-2">
                <span className="font-serif text-3xl text-navy leading-none">{value}</span>
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-navy/40">{suffix}</span>
            </div>
            {typeof trend === "number" && trend !== 0 && (
                <div className={`mt-2 font-mono text-[10px] tracking-[0.22em] uppercase ${trend < 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {trend < 0 ? "▼" : "▲"} {Math.abs(trend)} pts vs baseline
                </div>
            )}
        </div>
    );
}
